import type { ColumnConfig, ColumnGroupConfig } from '../types';

export interface MultiLevelHeaderStructure {
  groupHeaders: Array<{
    groupName: string;
    groupLabel: string;
    startIndex: number;
    endIndex: number;
    cssClass?: string;
    alignment?: 'left' | 'center' | 'right';
    width?: string;
  }>;
  columnHeaders: Array<{
    columnName: string;
    field: string;
    groupName?: string;
    columnConfig: ColumnConfig;
  }>;
}

/**
 * Creates a multi-level header structure from column groups and individual columns
 */
export function createMultiLevelHeaderStructure(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroupConfig[]
): MultiLevelHeaderStructure {
  const groupHeaders: MultiLevelHeaderStructure['groupHeaders'] = [];
  const columnHeaders: MultiLevelHeaderStructure['columnHeaders'] = [];
  
  if (!columnGroups || columnGroups.length === 0) {
    // No groups, return flat structure
    columns.forEach((column, index) => {
      columnHeaders.push({
        columnName: column.columnName,
        field: column.field || column.columnName,
        columnConfig: column
      });
    });
    
    return { groupHeaders, columnHeaders };
  }

  // Sort groups by order if specified
  const sortedGroups = [...columnGroups].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  let currentIndex = 0;
  
  // Process grouped columns
  sortedGroups.forEach(group => {
    if (!group.visible) return;
    
    const groupColumns = columns.filter(col => 
      group.columns.includes(col.field || col.columnName)
    );
    
    if (groupColumns.length > 0) {
      const startIndex = currentIndex;
      const endIndex = currentIndex + groupColumns.length - 1;
      
      groupHeaders.push({
        groupName: group.groupName,
        groupLabel: group.groupLabel,
        startIndex,
        endIndex,
        cssClass: group.cssClass,
        alignment: group.alignment,
        width: group.width
      });
      
      groupColumns.forEach(column => {
        columnHeaders.push({
          columnName: column.columnName,
          field: column.field || column.columnName,
          groupName: group.groupName,
          columnConfig: column
        });
        currentIndex++;
      });
    }
  });
  
  // Process ungrouped columns
  const groupedColumnFields = new Set(
    sortedGroups.flatMap(group => group.columns)
  );
  
  const ungroupedColumns = columns.filter(col => 
    !groupedColumnFields.has(col.field || col.columnName)
  );
  
  ungroupedColumns.forEach(column => {
    columnHeaders.push({
      columnName: column.columnName,
      field: column.field || column.columnName,
      columnConfig: column
    });
    currentIndex++;
  });
  
  return { groupHeaders, columnHeaders };
}

/**
 * Flattens a multi-level column structure for TanStack Table
 */
export function flattenColumnsForTable(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroupConfig[]
): ColumnConfig[] {
  const { columnHeaders } = createMultiLevelHeaderStructure(columns, columnGroups);
  return columnHeaders.map(header => header.columnConfig);
}

/**
 * Gets the column span for a group header
 */
export function getGroupColumnSpan(
  groupName: string,
  columnHeaders: MultiLevelHeaderStructure['columnHeaders']
): number {
  return columnHeaders.filter(header => header.groupName === groupName).length;
}

/**
 * Checks if a column is part of a group
 */
export function isColumnInGroup(
  columnField: string,
  columnGroups?: ColumnGroupConfig[]
): string | null {
  if (!columnGroups) return null;
  
  for (const group of columnGroups) {
    if (group.columns.includes(columnField)) {
      return group.groupName;
    }
  }
  
  return null;
}

/**
 * Gets the group configuration for a column
 */
export function getColumnGroup(
  columnField: string,
  columnGroups?: ColumnGroupConfig[]
): ColumnGroupConfig | null {
  if (!columnGroups) return null;
  
  return columnGroups.find(group => group.columns.includes(columnField)) || null;
}

/**
 * Creates a default column group configuration for NAADU/NEDU style grouping
 */
export function createDefaultColumnGroups(): ColumnGroupConfig[] {
  return [
    {
      groupName: 'naadu',
      groupLabel: 'NAADU',
      columns: ['naadu_long_view', 'naadu_short_view'],
      cssClass: 'bg-blue-50 text-blue-900 font-semibold',
      alignment: 'center',
      order: 1
    },
    {
      groupName: 'nedu',
      groupLabel: 'NEDU',
      columns: ['nedu_long_view', 'nedu_short_view'],
      cssClass: 'bg-green-50 text-green-900 font-semibold',
      alignment: 'center',
      order: 2
    }
  ];
}

/**
 * Validates column group configuration
 */
export function validateColumnGroups(
  columns: ColumnConfig[],
  columnGroups?: ColumnGroupConfig[]
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!columnGroups || columnGroups.length === 0) {
    return { isValid: true, errors: [] };
  }
  
  const allColumnFields = new Set(
    columns.map(col => col.field || col.columnName)
  );
  
  columnGroups.forEach((group, index) => {
    if (!group.groupName) {
      errors.push(`Group ${index + 1}: groupName is required`);
    }
    
    if (!group.groupLabel) {
      errors.push(`Group ${index + 1}: groupLabel is required`);
    }
    
    if (!group.columns || group.columns.length === 0) {
      errors.push(`Group ${index + 1}: columns array is required and cannot be empty`);
    }
    
    group.columns.forEach(columnField => {
      if (!allColumnFields.has(columnField)) {
        errors.push(`Group ${index + 1}: Column '${columnField}' does not exist in the columns array`);
      }
    });
  });
  
  // Check for duplicate column assignments
  const columnAssignments = new Map<string, string[]>();
  columnGroups.forEach(group => {
    group.columns.forEach(columnField => {
      if (!columnAssignments.has(columnField)) {
        columnAssignments.set(columnField, []);
      }
      columnAssignments.get(columnField)!.push(group.groupName);
    });
  });
  
  columnAssignments.forEach((groups, columnField) => {
    if (groups.length > 1) {
      errors.push(`Column '${columnField}' is assigned to multiple groups: ${groups.join(', ')}`);
    }
  });
  
  return { isValid: errors.length === 0, errors };
}
